import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAnalyticsData } from "../hooks/useAnalyticsData";
import { useWalletProjection } from "../hooks/useWalletProjection";
import { exportDetailedReport } from "../utils/csvExport";
import { ActivityFeed } from "../components/ActivityFeed";
import { WalletItem } from "../types";
import "../styles/Analytics.css";

export function AnalyticsDashboard() {
  const auth = getAuth();
  const db = getFirestore();
  const [walletItems, setWalletItems] = useState<WalletItem[] | null>(null);
  const [itemsLoading, setItemsLoading] = useState(true);

  const projection = useWalletProjection(auth.currentUser?.uid || null);
  const { data: analyticsData, loading: analyticsLoading } = useAnalyticsData(
    walletItems,
    projection.projection?.recentTransactions || null
  );

  // Load wallet items
  useEffect(() => {
    loadWalletItems();
  }, []);

  const loadWalletItems = async () => {
    try {
      setItemsLoading(true);
      const user = auth.currentUser;
      if (!user) {
        return;
      }

      const itemsCollection = collection(db, "wallet_items");
      const q = query(
        itemsCollection,
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(q);
      const items: WalletItem[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        items.push({
          walletItemId: doc.id,
          userId: data.userId,
          type: data.type,
          value: data.value,
          status: data.status,
          createdAt: data.createdAt?.toDate(),
          expiresAt: data.expiresAt?.toDate(),
          claimedAt: data.claimedAt?.toDate(),
          usedAmount: data.usedAmount || 0,
          count: data.count,
          greenFeeType: data.greenFeeType,
        });
      });

      setWalletItems(items);
    } catch (error) {
      console.error("Error loading wallet items:", error);
    } finally {
      setItemsLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState<"overview" | "details">(
    "overview"
  );

  const loading = itemsLoading || analyticsLoading;

  if (loading) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  if (!analyticsData || !projection.projection) {
    return <div className="analytics-empty">No data available</div>;
  }

  const COLORS = ["#667eea", "#764ba2", "#f093fb", "#4facfe", "#00f2fe"];

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="analytics-header">
        <h1>💹 Analytics Dashboard</h1>
        <button
          className="export-analytics-btn"
          onClick={() => {
            const userName = "User";
            exportDetailedReport(projection.projection?.recentTransactions || [], walletItems || [], userName);
          }}
        >
          📊 Export Report
        </button>
      </div>

      {/* Tabs */}
      <div className="analytics-tabs">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === "details" ? "active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          Details
        </button>
      </div>

      {activeTab === "overview" && (
        <div className="analytics-overview">
          {/* Statistics Cards */}
          <div className="stats-grid">
            <div className="stat-card income">
              <div className="stat-icon">➕</div>
              <div className="stat-content">
                <div className="stat-label">Total Issued</div>
                <div className="stat-value">{analyticsData.totalIssued} NOK</div>
              </div>
            </div>

            <div className="stat-card spent">
              <div className="stat-icon">💳</div>
              <div className="stat-content">
                <div className="stat-label">Total Redeemed</div>
                <div className="stat-value">{analyticsData.totalRedeemed} NOK</div>
              </div>
            </div>

            <div className="stat-card expired">
              <div className="stat-icon">⏱</div>
              <div className="stat-content">
                <div className="stat-label">Total Expired</div>
                <div className="stat-value">{analyticsData.totalExpired} NOK</div>
              </div>
            </div>

            <div className="stat-card balance">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <div className="stat-label">Remaining Balance</div>
                <div className="stat-value">
                  {analyticsData.totalIssued - analyticsData.totalRedeemed} NOK
                </div>
              </div>
            </div>
          </div>

          {/* Balance Over Time Chart */}
          <div className="chart-container">
            <h2>Balance Over Time (30 Days)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={analyticsData.balanceOverTime}
                margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  stroke="#999"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#999" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number | undefined) => value ? `${value} NOK` : ""}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke="#667eea"
                  dot={false}
                  strokeWidth={2}
                  name="Balance (NOK)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Two Charts Side by Side */}
          <div className="charts-row">
            {/* Spending by Category */}
            <div className="chart-container half">
              <h2>Spending by Category</h2>
              {analyticsData.spendingByCategory.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analyticsData.spendingByCategory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#999" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#999" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                      }}
                      formatter={(value: number | undefined) => value ? `${value} NOK` : ""}
                    />
                    <Bar dataKey="value" fill="#764ba2" name="Amount (NOK)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No spending data</div>
              )}
            </div>

            {/* Transaction Distribution */}
            <div className="chart-container half">
              <h2>Transaction Distribution</h2>
              {analyticsData.transactionDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={analyticsData.transactionDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {analyticsData.transactionDistribution.map(
                        (entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        )
                      )}
                    </Pie>
                    <Tooltip
                      formatter={(value: number | undefined) => value ? `${value} transactions` : ""}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No transaction data</div>
              )}
            </div>
          </div>

          {/* Activity Feed */}
          <div className="activity-feed-section">
            <ActivityFeed maxItems={15} showHeader={true} />
          </div>
        </div>
      )}

      {activeTab === "details" && (
        <div className="analytics-details">
          <div className="details-grid">
            {/* Wallet Items Stats */}
            <div className="details-section">
              <h2>📋 Wallet Items</h2>
              <div className="detail-item">
                <span className="detail-label">Active Items</span>
                <span className="detail-value active">{analyticsData.activeItems}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Redeemed Items</span>
                <span className="detail-value redeemed">
                  {analyticsData.redeemedItems}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Expired Items</span>
                <span className="detail-value expired">{analyticsData.expiredItems}</span>
              </div>
              <div className="detail-item total">
                <span className="detail-label">Total Items</span>
                <span className="detail-value">
                  {analyticsData.activeItems +
                    analyticsData.redeemedItems +
                    analyticsData.expiredItems}
                </span>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="details-section">
              <h2>💰 Financial Summary</h2>
              <div className="detail-item">
                <span className="detail-label">Total Issued</span>
                <span className="detail-value">
                  {analyticsData.totalIssued} NOK
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Total Redeemed</span>
                <span className="detail-value">
                  {analyticsData.totalRedeemed} NOK
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Total Expired</span>
                <span className="detail-value">
                  {analyticsData.totalExpired} NOK
                </span>
              </div>
              <div className="detail-item total">
                <span className="detail-label">Remaining Balance</span>
                <span className="detail-value">
                  {analyticsData.totalIssued - analyticsData.totalRedeemed} NOK
                </span>
              </div>
            </div>

            {/* Spending Breakdown */}
            <div className="details-section full">
              <h2>📊 Spending Breakdown by Category</h2>
              {analyticsData.spendingByCategory.length > 0 ? (
                <div className="breakdown-list">
                  {analyticsData.spendingByCategory.map((category) => (
                    <div key={category.name} className="breakdown-item">
                      <span className="category-name">{category.name}</span>
                      <span className="category-value">{category.value} NOK</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No spending data available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
